import { gql } from "@apollo/client";
import client from "../apolloClient";
import type {
  BrandScreenMap,
  Campaign,
  CampaignFilters,
  CampaignPlacementsResponse,
  CampaignScreensFilters,
  CampaignSummary,
  CampaignsResponse,
} from "../../schemas/campaignSchema";

const PAGINATION_FIELDS = `
  page
  pageSize
  total
  totalPages
  hasNextPage
  hasPrevPage
`;

const CAMPAIGN_FIELDS = `
  creativeId
  name
  description
  fileUrl
  thumbnailUrl
  fileType
  duration
  startDate
  endDate
  status
  screenCount
  activeScreenCount
  liveScreenCount
  scheduleStartDate
  scheduleEndDate
  totalPlays
  totalPlayDuration
  lastPlayedAt
`;

const GET_BRAND_CAMPAIGNS = gql`
  query GetBrandCampaigns(
    $page: Int
    $pageSize: Int
    $search: String
    $status: CampaignStatus
  ) {
    brandCampaigns(
      page: $page
      pageSize: $pageSize
      search: $search
      status: $status
    ) {
      campaigns {
        ${CAMPAIGN_FIELDS}
      }
      ${PAGINATION_FIELDS}
    }
  }
`;

const GET_BRAND_CAMPAIGN = gql`
  query GetBrandCampaign($creativeId: ID!) {
    brandCampaign(creativeId: $creativeId) {
      ${CAMPAIGN_FIELDS}
    }
  }
`;

const GET_BRAND_CAMPAIGN_SCREENS = gql`
  query GetBrandCampaignScreens(
    $creativeId: ID!
    $page: Int
    $pageSize: Int
    $search: String
    $status: CampaignStatus
  ) {
    brandCampaignScreens(
      creativeId: $creativeId
      page: $page
      pageSize: $pageSize
      search: $search
      status: $status
    ) {
      placements {
        id
        scheduleId
        screenId
        screenName
        restaurantName
        city
        region
        address
        orientation
        playlistId
        playlistName
        mainPlaylistId
        mainPlaylistName
        startDate
        endDate
        startTime
        endTime
        daysOfWeek
        status
        isPlayingNow
        screenOnline
        lastSeenAt
        plays
        playDuration
        lastPlayedAt
      }
      ${PAGINATION_FIELDS}
    }
  }
`;

const GET_BRAND_CAMPAIGN_SUMMARY = gql`
  query GetBrandCampaignSummary {
    brandCampaignSummary {
      totalCampaigns
      activeCampaigns
      upcomingCampaigns
      expiredCampaigns
      scheduledCampaigns
      totalScreens
      liveScreens
      totalPlays
      totalPlayDuration
    }
  }
`;

const GET_BRAND_SCREEN_MAP = gql`
  query GetBrandScreenMap {
    brandScreenMap {
      truncated
      screens {
        screenId
        screenName
        restaurantName
        city
        region
        address
        latitude
        longitude
        orientation
        screenOnline
        lastSeenAt
        campaigns {
          creativeId
          name
          thumbnailUrl
          fileType
          status
          isPlayingNow
          plays
          playDuration
          lastPlayedAt
          slots {
            startDate
            endDate
            startTime
            endTime
            daysOfWeek
            status
          }
        }
      }
    }
  }
`;

/**
 * Every one of these is scoped to the signed-in brand by the backend, from the
 * verified token — there is deliberately no brandId argument on the wire for
 * the brand portal to get wrong.
 *
 * Reads are network-only: a campaign carries live state (which screens are
 * playing it right now), so a cached answer would be quietly stale.
 */
export const campaignService = {
  getAll: async (filters: CampaignFilters = {}): Promise<CampaignsResponse> => {
    const { status, ...rest } = filters;
    const { data } = await client.query({
      query: GET_BRAND_CAMPAIGNS,
      variables: { ...rest, status: status || undefined },
      fetchPolicy: "network-only",
    });
    return data.brandCampaigns;
  },

  getById: async (creativeId: string): Promise<Campaign | null> => {
    const { data } = await client.query({
      query: GET_BRAND_CAMPAIGN,
      variables: { creativeId },
      fetchPolicy: "network-only",
    });
    return data.brandCampaign ?? null;
  },

  getScreens: async ({
    creativeId,
    status,
    ...rest
  }: CampaignScreensFilters): Promise<CampaignPlacementsResponse> => {
    const { data } = await client.query({
      query: GET_BRAND_CAMPAIGN_SCREENS,
      variables: { creativeId, ...rest, status: status || undefined },
      fetchPolicy: "network-only",
    });
    return data.brandCampaignScreens;
  },

  getSummary: async (): Promise<CampaignSummary> => {
    const { data } = await client.query({
      query: GET_BRAND_CAMPAIGN_SUMMARY,
      fetchPolicy: "network-only",
    });
    return data.brandCampaignSummary;
  },

  getScreenMap: async (): Promise<BrandScreenMap> => {
    const { data } = await client.query({
      query: GET_BRAND_SCREEN_MAP,
      fetchPolicy: "network-only",
    });
    return data.brandScreenMap;
  },
};
